import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  CircularProgress,
  Grid,
  TextField,
  Avatar,
  Chip,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { useDispatch } from "react-redux";
import AddEditStaffDialog from "./addEditStaffDilog";
import { useMutation, useQuery } from "@apollo/client";
import DeleteConfirmationDialog from "./deleteConfirmationDialog";

import { hasPermission, Permissions } from "../../../../../platform/util/accessControl";
import { UserContext } from "../../../../../platform/components/auth/auth-context";
import { useSelectedBusiness } from "../../../../../platform/redux/store";
import { Staff } from "../../../../../platform/types";
import {
  DELETE_STAFF,
  POST_STAFF,
  UPDATE_STAFF,
} from "../../../../../platform/graphql/mutations";
import { GET_BILLING_SUBSCRIPTION, GET_PLANS } from "../../../../../platform/graphql/queries";
import {
  deleteStaff,
  updateStaff,
} from "../../../../../platform/redux/actions/businessActions";
import UpgradePlan from "../../../../../platform/util/upgrade/upgradePlan";
import StaffDetailHorizontalTab from "./staffDetailHorizontalTab";
import { StaffStatus, getEffectivePlan, getTrialDaysLeft } from "../../../../../platform/util/utils";
import { validateStaffRoleChangeChecklist } from "../../../../../platform/util/roleValidation";

export default function StaffListVerticalTab() {
  const userContext = useContext(UserContext);
  const providerUser = userContext.providerUser;

  const dispatch = useDispatch();
  const selectedBusiness = useSelectedBusiness();
  const isPersonalWorkspace = selectedBusiness?.businessBasic?.workspaceType === "PERSONAL";
  const businessStaffs = useMemo(() => isPersonalWorkspace
    ? selectedBusiness?.businessStaffs?.filter(staff => Number(staff?.user?.id) === Number(providerUser?.userId))
    : selectedBusiness?.businessStaffs,
  [selectedBusiness?.businessStaffs, isPersonalWorkspace, providerUser?.userId]);

  const [selectedStaff, setSelectedStaff] = useState<Staff | null>(null);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteConfirmationName, setDeleteConfirmationName] = useState("");
  const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [q, setQ] = useState("");

  const standardPlanRequired = 2;
  const staffs = businessStaffs;
  const businessId = selectedBusiness?.businessBasic.id;

  const [deleteStaffDB] = useMutation(DELETE_STAFF);
  const [postStaff] = useMutation(POST_STAFF);
  const [updateStaffDB] = useMutation(UPDATE_STAFF);
  const { data: billingData } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId },
    skip: !businessId,
    fetchPolicy: "network-only",
  });
  const { data: plansData } = useQuery(GET_PLANS, {
    skip: !businessId,
  });
  const tokenPlan = Number(getEffectivePlan(providerUser) || 0);
  const sub = billingData?.getBillingSubscription;
  const subPlanCode = String(
    plansData?.plans?.find((p: any) => String(p.id) === String(sub?.planId))?.code || ""
  ).toUpperCase();
  const subscriptionPlan =
    sub?.status === "active" || sub?.status === "canceling"
      ? subPlanCode === "ENTERPRISE"
        ? 4
        : subPlanCode === "PREMIUM"
          ? 3
          : subPlanCode === "STANDARD"
            ? 2
            : 1
      : 0;
  const effectiveAccessPlan = Math.max(tokenPlan, subscriptionPlan);
  const hasStandardPlan = effectiveAccessPlan >= standardPlanRequired;
  const trialDaysLeft = getTrialDaysLeft(providerUser || {});
  const showTrialEndingSoon = Number(trialDaysLeft) > 0 && Number(trialDaysLeft) <= 5;

  const getApolloErrorMessage = (error: any, fallback: string) => {
    const graphQLError = error?.graphQLErrors?.[0]?.message;
    const networkError = error?.networkError?.message;
    return graphQLError || networkError || error?.message || fallback;
  };

  // Initialize/keep selection valid
  useEffect(() => {
    if (!staffs || staffs.length === 0) return;

    // 1️⃣ Prefer logged-in staff
    const myStaff =
      staffs.find((s) => Number(s.id) === Number(providerUser.staffId)) ||
      staffs[0];

    setSelectedStaff(myStaff);
  }, [staffs, providerUser.staffId]);

  useEffect(() => {
    if (selectedStaff || !staffs || staffs.length === 0) return;
    const myStaff =
      staffs.find((s) => Number(s.id) === Number(providerUser.staffId)) ||
      staffs[0];
    setSelectedStaff(myStaff);
  }, [selectedStaff, staffs, providerUser.staffId]);

  const handleDeleteClick = (staff: Staff | null) => {
    setDeleteConfirmationName(staff?.user?.username ?? "");
    setSelectedStaff(staff);
    setDeleteConfirmationOpen(true);
  };
  const handleDeleteConfirmationClose = () => {
    setDeleteConfirmationName(selectedStaff?.user?.username ?? "");
    setDeleteConfirmationOpen(false);
  };
  const handleDialogClose = () => setErrorDialogOpen(false);

  const handleOpenAddDialog = (staff?: Staff) => {
    if (isPersonalWorkspace && !staff) return;
    if (!hasStandardPlan) {
      setCheckPlanNeeded(true);
      return;
    }
    setSelectedStaff(staff || null);
    setIsEditMode(!!staff);
    setOpenAddDialog(true);
  };
  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleAddStaff = async (newStaff: Staff) => {
    const { user, role, ...rest } = newStaff;
    const email = user?.email || "";
    const name = user?.username || "";
    const addChecklist = validateStaffRoleChangeChecklist({
      staffs: staffs || [],
      roles: selectedBusiness?.roles || [],
      action: "add",
      nextRole: newStaff?.role,
    });
    if (addChecklist.warnings.length > 0) {
      console.warn("Role validation warnings:", addChecklist.warnings);
    }
    if (!addChecklist.valid) {
      throw new Error(addChecklist.errors[0] || "Invalid role selection.");
    }
    try {
      const res = await postStaff({
        variables: {
          businessId: businessId,
          businessStaff: {
            ...rest,
            roleId: newStaff?.role?.id,
            email,
            name,
          },
        },
      });
      const staffData = res.data?.createStaff;
      if (staffData) {
        dispatch(updateStaff(businessId ?? -1, staffData));
        setSelectedStaff(staffData);
        setOpenAddDialog(false);
        return staffData;
      }
      throw new Error("Failed to add staff.");
    } catch (error) {
      console.error("Error adding staff:", error);
      throw new Error(getApolloErrorMessage(error, "Failed to add staff."));
    }
  };

  const handleDeleteStaff = () => {
    const deleteChecklist = validateStaffRoleChangeChecklist({
      staffs: staffs || [],
      roles: selectedBusiness?.roles || [],
      action: "delete",
      targetStaff: selectedStaff,
    });
    if (deleteChecklist.warnings.length > 0) {
      console.warn("Role validation warnings:", deleteChecklist.warnings);
    }
    if (!deleteChecklist.valid) {
      handleDeleteConfirmationClose();
      setErrorDialogOpen(true);
      return;
    }
    handleDeleteConfirmationClose();

    deleteStaffDB({
      variables: { staffId: selectedStaff?.id },
    }).then((res: any) => {
      const deletedStaff = res.data?.deleteStaff;
      if (deletedStaff) {
        const newSelectedStaff = staffs?.filter(
          (s: Staff) => s.id !== selectedStaff?.id
        )[0];
        setSelectedStaff(newSelectedStaff || null);
        businessId && dispatch(deleteStaff(businessId, deletedStaff?.id ?? -1));
      }
    });
  };

  const handleUpdateStaff = async (staff: Staff) => {
    const updateChecklist = validateStaffRoleChangeChecklist({
      staffs: staffs || [],
      roles: selectedBusiness?.roles || [],
      action: "update",
      targetStaff: selectedStaff,
      nextRole: staff?.role,
    });
    if (updateChecklist.warnings.length > 0) {
      console.warn("Role validation warnings:", updateChecklist.warnings);
    }
    if (!updateChecklist.valid) {
      setErrorDialogOpen(true);
      throw new Error(updateChecklist.errors[0] || "Invalid role change.");
    }
    try {
      const res = await updateStaffDB({
        variables: {
          staffId: staff.id,
          businessStaff: {
            email: staff?.user?.email,
            name: staff?.user?.username,
            initials: staff?.initials,
            roleId: staff.role?.id,
            url: staff.url,
          },
        },
      });
      const updatedStaff = res.data?.updateStaff;
      if (!updatedStaff) {
        throw new Error("Failed to update staff.");
      }
      businessId &&
        updateStaff &&
        dispatch(updateStaff(businessId, updatedStaff));
      const updatedIndex =
        staffs?.findIndex((s) => s.id === updatedStaff.id) ?? -1;
      if (updatedIndex !== -1) {
        const refreshed = staffs?.[updatedIndex];
        setSelectedStaff(refreshed || null);
      }
      return updatedStaff;
    } catch (error: any) {
      console.error("Error updating staff:", error);
      throw new Error(getApolloErrorMessage(error, "Failed to update staff."));
    }
  };

  const handleSaveUpdateStaff =
    selectedStaff && isEditMode ? handleUpdateStaff : handleAddStaff;

  const currentLoginStaff = businessStaffs?.find(
    (s) => Number(s.id) === providerUser.staffId
  );
  const isCurrentAdmin =
    String(currentLoginStaff?.role?.name || "").trim().toUpperCase() === "ADMIN";
  const userPermissions = currentLoginStaff?.role?.permissions || [];

  const requiredEditCalendarPermission = Permissions.Calendar.EDIT;
  const canEditCalendar = hasPermission(
    userPermissions,
    requiredEditCalendarPermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );
  const requiredEditBookingTypePermission = Permissions.Calendar.EDIT;
  const canEditBookingType = hasPermission(
    userPermissions,
    requiredEditBookingTypePermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );
  const requiredAddPermission = Permissions.Staff.ADD;
  const canAddStaff = hasPermission(
    userPermissions,
    requiredAddPermission,
    providerUser.staffId,
    providerUser.staffId
  );
  const requiredImagePermission = Permissions.Staff.EDIT_IMAGE_OWN;
  const canEditOwnImageStaff = hasPermission(
    userPermissions,
    requiredImagePermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );
  const canEditImageByGeneralStaffEdit = hasPermission(
    userPermissions,
    Permissions.Staff.EDIT,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );
  const canEditImageStaff = canEditOwnImageStaff || canEditImageByGeneralStaffEdit;
  const requiredIntegerationPermission =
    Permissions.Staff.EDIT_INTEGERATION_OWN;
  const canEditIntegerationStaff = hasPermission(
    userPermissions,
    requiredIntegerationPermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );
  const requiredEditPermission = Permissions.Staff.EDIT;
  const canEditStaff = hasPermission(
    userPermissions,
    requiredEditPermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );
  const requiredDeletePermission = Permissions.Staff.DELETE;
  const canDeleteStaff = hasPermission(
    userPermissions,
    requiredDeletePermission,
    providerUser.staffId,
    Number(selectedStaff?.id)
  );

  const filteredStaffs = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return businessStaffs || [];
    return (businessStaffs || []).filter((s) => {
      const name = s?.user?.username?.toLowerCase() || "";
      const email = s?.user?.email?.toLowerCase() || "";
      const role = s?.role?.name?.toLowerCase() || "";
      return name.includes(term) || email.includes(term) || role.includes(term);
    });
  }, [businessStaffs, q]);

  const totalUsers = businessStaffs?.length ?? 0;
  const activeUsers =
    (businessStaffs || []).filter((s) => Number(s?.status) === StaffStatus.ACTIVE).length;
  const pendingUsers =
    (businessStaffs || []).filter((s) => Number(s?.status) === StaffStatus.PENDING).length;
  const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] ?? "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase() || first.toUpperCase() || "?";
  };

  if (isPersonalWorkspace) {
    const ownStaff = businessStaffs?.[0];
    return <Box sx={{ p: { xs: 1.5, sm: 3 }, width: "100%", boxSizing: "border-box" }}>
      <Typography component="h1" variant="h5" sx={{ mb: 1 }}>My profile</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Manage your profile, availability, documents, images, and calendar connections.</Typography>
      {ownStaff && <Button startIcon={<EditOutlinedIcon />} disabled={!canEditStaff || selectedStaff?.id !== ownStaff.id}
        onClick={() => handleOpenAddDialog(ownStaff)} sx={{ mb: 2 }}>Edit profile</Button>}
      {checkPlanNeeded && <UpgradePlan featureName="Profile management" requiredPlanId={standardPlanRequired} onClose={() => setCheckPlanNeeded(false)} />}
      {ownStaff && <AddEditStaffDialog open={openAddDialog} handleClose={handleCloseAddDialog}
        handleSaveUpdateStaff={handleUpdateStaff} staff={ownStaff} />}
      {!ownStaff ? <Alert severity="info">Your personal profile is not available. Refresh the page to try again.</Alert>
        : selectedStaff?.id !== ownStaff.id ? <CircularProgress />
        : <StaffDetailHorizontalTab
          key={ownStaff.id}
          isStaffCanEditItsImage={canEditImageStaff}
          isStaffCanEditItsEmailIntegeration={!canEditIntegerationStaff}
          isStaffDisabled={canEditStaff}
          isCalenderDisabled={!canEditCalendar}
          isBookingTypeDisabled={!canEditBookingType}
          businessStaff={ownStaff}
          totalStaffCount={1}
        />}
    </Box>;
  }

  if (!businessStaffs || businessStaffs.length === 0) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            No staff yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add your first team member to get started. If they need to manage the business without being the owner, assign the Admin role.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenAddDialog()}
            disabled={!canAddStaff}
          >
            Add Team Member
          </Button>
        </Box>
      </Box>
    );
  }

  if (!selectedStaff) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {checkPlanNeeded && (
        <UpgradePlan
          featureName="Staff management"
          requiredPlanId={standardPlanRequired}
          onClose={() => setCheckPlanNeeded(false)}
        />
      )}
      {showTrialEndingSoon && (
        <Box sx={{ px: { xs: 1.5, sm: 3, md: 5 }, pt: 1.5 }}>
          <Alert severity="warning">
            Trial period ends in {trialDaysLeft} day{Number(trialDaysLeft) === 1 ? "" : "s"}.
            Staff access requires Standard plan after expiry.
          </Alert>
        </Box>
      )}
      {!hasStandardPlan && !isCurrentAdmin && (
        <Box sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Staff operations are restricted on your current plan. Please contact your Admin.
          </Alert>
          <Button variant="contained" onClick={() => setCheckPlanNeeded(true)}>
            Upgrade Plan
          </Button>
        </Box>
      )}
      {(!hasStandardPlan && isCurrentAdmin) && (
        <Box sx={{ px: { xs: 1.5, sm: 3, md: 5 }, pt: 1.5 }}>
          <Alert severity="info">
            Free/expired mode: Admin access only. Non-admin staff operations are blocked.
          </Alert>
        </Box>
      )}
      {(hasStandardPlan || isCurrentAdmin) && (
      <>
      <Dialog open={errorDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Validation Error</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cannot delete or update the Admin status for the last admin user.
            Please add another staff member and assign them the admin role before
            performing this operation.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          boxSizing: "border-box",
          px: { xs: 1.5, sm: 3, md: 5 },
          py: { xs: 1, sm: 3, md: 5 },
        }}
      >
        <Box
          sx={{
            mb: 1.5,
            p: 1.25,
            borderRadius: 2,
            border: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}>
              Team Members
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {totalUsers} total, {activeUsers} active, {pendingUsers} pending
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenAddDialog()}
            disabled={!canAddStaff}
          >
            Add Team Member
          </Button>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Need someone to operate this business without owning it? Add them here and assign the <strong>Admin</strong> role.
        </Alert>

        <AddEditStaffDialog
          open={openAddDialog}
          handleClose={handleCloseAddDialog}
          handleSaveUpdateStaff={handleSaveUpdateStaff}
          staff={isEditMode ? selectedStaff : null}
        />

        <Grid container spacing={2} sx={{ width: "100%", m: 0 }}>
          {/* SINGLE CONTENT COLUMN — user switcher lives in the header on the right */}
          <Grid item xs={12}>
            {/* Selected staff identity header with inline user switcher */}
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexWrap: "wrap",
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 0.75,
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>
                  Select team member
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                  <Chip
                    label={`Selected: ${selectedStaff?.user?.username || "-"}`}
                    size="small"
                    sx={{ bgcolor: "action.hover", fontWeight: 700 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => handleOpenAddDialog(selectedStaff)}
                    disabled={!canEditStaff}
                  >
                    Edit Profile
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => handleDeleteClick(selectedStaff)}
                    disabled={!canDeleteStaff}
                  >
                    Delete Profile
                  </Button>
                </Box>
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder="Search staff by name, email, or role..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                sx={{ mb: 0.25 }}
              />
              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(auto-fill, minmax(164px, 1fr))",
                  },
                  gap: 1,
                  maxHeight: { xs: 320, sm: 360 },
                  overflowY: "auto",
                  overflowX: "hidden",
                  pb: 0.5,
                }}
              >
                {filteredStaffs.map((s) => {
                  const isActiveCard = Number(selectedStaff?.id) === Number(s?.id);
                  const username = s?.user?.username || "Staff";
                  const roleName = s?.role?.name || "Staff";
                  const isPending = Number(s?.status) === StaffStatus.PENDING;
                  return (
                    <Button
                      key={s?.id}
                      variant={isActiveCard ? "contained" : "outlined"}
                      disableRipple
                      onClick={() => setSelectedStaff(s)}
                      sx={{
                        width: "100%",
                        minWidth: 0,
                        px: 0.9,
                        py: 0.75,
                        borderRadius: 2.25,
                        textTransform: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        gap: 0.4,
                        bgcolor: isActiveCard ? "#22374a" : "background.paper",
                        color: isActiveCard ? "#fff" : "text.primary",
                        borderColor: isActiveCard ? "#22374a" : "#d5deea",
                        WebkitTapHighlightColor: "transparent",
                        boxShadow: isActiveCard
                          ? "0 10px 20px rgba(34,55,74,0.24)"
                          : "0 3px 8px rgba(17,24,39,0.08)",
                        transition: "box-shadow 120ms ease, border-color 120ms ease, background-color 120ms ease",
                        "&:hover": {
                          borderColor: "#22374a",
                          boxShadow: isActiveCard
                            ? "0 14px 28px rgba(34,55,74,0.33)"
                            : "0 10px 20px rgba(17,24,39,0.12)",
                        },
                        "&:active": {
                          bgcolor: isActiveCard ? "#22374a" : "background.paper",
                          borderColor: "#22374a",
                        },
                        "&.Mui-focusVisible": {
                          bgcolor: isActiveCard ? "#22374a" : "background.paper",
                          borderColor: "#22374a",
                        },
                      }}
                    >
                      {s?.image?.url ? (
                        <img
                          src={s.image.url}
                          alt={username}
                          draggable={false}
                          style={{
                            width: 58,
                            height: 58,
                            borderRadius: "50%",
                            objectFit: "cover",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            WebkitTouchCallout: "none",
                            pointerEvents: "none",
                            border: isActiveCard
                              ? "2px solid rgba(255,255,255,0.95)"
                              : "2px solid rgba(35,55,74,0.18)",
                            boxShadow: "0 6px 14px rgba(15,23,42,0.2)",
                          }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 58,
                            height: 58,
                            boxShadow: "0 6px 14px rgba(15,23,42,0.2)",
                            userSelect: "none",
                            WebkitUserSelect: "none",
                            WebkitTouchCallout: "none",
                            pointerEvents: "none",
                          }}
                        >
                          {(s?.initials || getInitials(username)).toUpperCase()}
                        </Avatar>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          width: "100%",
                          textAlign: "center",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          fontWeight: isActiveCard ? 800 : 700,
                          fontSize: 11.8,
                          lineHeight: 1.2,
                          minHeight: 20,
                        }}
                      >
                        {username}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: isActiveCard ? "rgba(255,255,255,0.95)" : "text.secondary",
                          fontWeight: 700,
                          fontSize: 10.2,
                          px: 0.75,
                          py: 0.12,
                          borderRadius: 999,
                          bgcolor: isActiveCard ? "rgba(255,255,255,0.18)" : "#eef3f9",
                        }}
                      >
                        {isPending ? "Pending" : roleName}
                      </Typography>
                    </Button>
                  );
                })}
                {filteredStaffs.length === 0 && (
                  <Box sx={{ gridColumn: "1 / -1", py: 2 }}>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No staff match your search.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Content for the selected user */}
            {selectedStaff && (
              <StaffDetailHorizontalTab
                isStaffCanEditItsImage={canEditImageStaff}
                isStaffCanEditItsEmailIntegeration={!canEditIntegerationStaff}
                isStaffDisabled={canEditStaff}
                isCalenderDisabled={!canEditCalendar}
                isBookingTypeDisabled={!canEditBookingType}
                businessStaff={selectedStaff}
                // (optional) you can pass totalStaffCount if you want the child to hide its own user UI (it doesn't show any)
                totalStaffCount={totalUsers}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <DeleteConfirmationDialog
        open={deleteConfirmationOpen}
        staffName={deleteConfirmationName}
        handleClose={handleDeleteConfirmationClose}
        handleDelete={handleDeleteStaff}
      />
      </>
      )}
    </>
  );
}

